import { useCallback } from 'react';
import { BadgeCheck, FileText, HelpCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Typography } from '@/components/ui/typography';
import { useNavigationService } from '../../../services/navigate-service';
import { ROUTES } from '../../../config/routes';

import PropertyCartTable from '../cart/components/PropertyCartTable';
import AssetCartTable from '../cart/components/AssetCartTable';
import CartSection from '../cart/components/CartSection';

import { useOrderConfirmationData } from './hooks/useOrderConfirmationData';
import type { GroupedAssets } from './hooks/useOrderConfirmationData';

const OrderConfirmationPage = () => {
  const { navigateTo } = useNavigationService();

  const {
    propertyCartItems,
    assetCartItems,
    hasPropertyItems,
    hasAssetItems,
    hasBothTypes,
    groupedAssets,
    propertyItemsByEntity,
    hasMultipleEntities,
  } = useOrderConfirmationData();

  const handleNavigateToOrders = useCallback(() => {
    navigateTo(ROUTES.ORDERS);
  }, [navigateTo]);

  const handleContactClick = useCallback(() => {
    navigateTo(ROUTES.HELP_CONTACT);
  }, [navigateTo]);

  const renderEntityPropertyTables = () =>
    propertyItemsByEntity.map((entity) => (
      <CartSection
        key={entity.entityUUID}
        title={entity.entityName}
        orderCount={entity.items.length}
        subtotal={entity.subtotal}
        defaultExpanded
      >
        <PropertyCartTable data={entity.items} readOnly={true} />
      </CartSection>
    ));

  const renderGroupedAssetSections = () =>
    groupedAssets.map((group: GroupedAssets) => (
      <CartSection
        key={group.propertyUUID}
        title={group.propertyAddress}
        subtitle={group.entityName}
        orderCount={group.items.length}
        subtotal={group.subtotal}
        defaultExpanded
        expandable={groupedAssets.length > 1}
      >
        <AssetCartTable data={group.items} readOnly={true} />
      </CartSection>
    ));

  const orderCountLabel = (count: number, kind: 'property' | 'asset') => {
    const noun = kind === 'property' ? 'On-site services' : 'Image Edits & Upgrades';
    const word = count === 1 ? 'order' : 'orders';
    return `${count} ${word} (${noun})`;
  };

  // Router state doesn't survive a refresh or a direct visit, so with no
  // order data the page renders a real empty state instead of a bare
  // "Order Confirmed" band over nothing.
  if (!hasPropertyItems && !hasAssetItems) {
    return (
      <section className="flex h-full w-full flex-col bg-background">
        <EmptyState
          icon={FileText}
          title="No Order to Display"
          description="This page shows an order right after checkout. Find past orders anytime in My Orders."
          action={
            <Button onClick={handleNavigateToOrders}>View my orders</Button>
          }
        />
      </section>
    );
  }

  return (
    <section className="flex h-full w-full flex-col bg-background">
      <div className="flex-1 overflow-y-auto bg-background">
        {/* Success header */}
        <div className="border-b border-border bg-background px-6 py-8">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success"
              aria-hidden="true"
            >
              <BadgeCheck className="h-9 w-9" />
            </div>
            <Typography as="h1" variant="heading-2xl">
              Order Confirmed
            </Typography>
            <Typography
              as="p"
              variant="body"
              className="text-muted-foreground"
            >
              Thank you for your order.
            </Typography>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleContactClick}
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
                Contact PlanOmatic
              </Button>
            </div>
          </div>
        </div>

        {/* Order tables */}
        {(hasPropertyItems || hasAssetItems) && (
          <section className="space-y-4 p-6">
            {hasBothTypes ? (
              <>
                <CartSection
                  title="On-site Services"
                  variant="outer"
                  defaultExpanded
                >
                  <div className="space-y-3 p-3">
                    {hasMultipleEntities ? (
                      renderEntityPropertyTables()
                    ) : (
                      <PropertyCartTable
                        data={propertyCartItems}
                        readOnly={true}
                      />
                    )}
                  </div>
                </CartSection>
                <CartSection
                  title="Image Edit and Upgrades"
                  variant="outer"
                  defaultExpanded
                >
                  <div className="space-y-3 p-3">
                    {renderGroupedAssetSections()}
                  </div>
                </CartSection>
              </>
            ) : (
              <>
                {hasPropertyItems && (
                  <div className="space-y-3">
                    <Typography as="h2" variant="body-medium">
                      {orderCountLabel(propertyCartItems.length, 'property')}
                    </Typography>
                    {hasMultipleEntities ? (
                      renderEntityPropertyTables()
                    ) : (
                      <PropertyCartTable
                        data={propertyCartItems}
                        readOnly={true}
                      />
                    )}
                  </div>
                )}
                {hasAssetItems && (
                  <div className="space-y-3">
                    <Typography as="h2" variant="body-medium">
                      {orderCountLabel(assetCartItems.length, 'asset')}
                    </Typography>
                    {renderGroupedAssetSections()}
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 flex justify-end border-t border-border bg-background px-5 py-3.5">
        <Button
          type="button"
          className="min-w-[140px]"
          onClick={handleNavigateToOrders}
        >
          View my orders
        </Button>
      </div>
    </section>
  );
};

export default OrderConfirmationPage;
